import EntityStatus from "../components/EntityStatus";
import DeactivateButton from "../components/DeactivateButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import EmployeeForm from "../components/EmployeeForm";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import {
  EmployeeFormValues,
  toEmployeeFormValues,
  toUpdateEmployeePayload,
} from "../lib/employeeForm";
import { fetchAllClientCompanies } from "../lib/clientCompanies";
import { readErrorMessage } from "../lib/http";
import { ClientCompany, Workstation } from "../types/clientCompanies";
import { Employee } from "../types/employees";

const defaultValues: EmployeeFormValues = {
  fullName: "",
  nationalId: "",
  locationType: "",
  clientCompanyId: "",
  workstationId: "",
  email: "",
  phone: "",
  gender: "",
  department: "",
  jobTitle: "",
  seaYears: "",
  landline: "",
  nationality: "",
  note: "",
};

function EmployeeEditPage() {
  const { id } = useParams<{ id: string }>();
  const { authorizedFetch, canMaintainEmployees, canManageClientCompanies } =
    useAppSession();
  const navigate = useNavigate();

  const [isLoadingEmployee, setIsLoadingEmployee] = useState(true);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [isLoadingWorkstations, setIsLoadingWorkstations] = useState(false);
  const [deletedAt, setDeletedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadCompaniesError, setLoadCompaniesError] = useState<string | null>(
    null,
  );
  const [loadWorkstationsError, setLoadWorkstationsError] = useState<
    string | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [employeeCompany, setEmployeeCompany] = useState<ClientCompany | null>(
    null,
  );

  const form = useForm<EmployeeFormValues>({ defaultValues });
  const { getValues, reset, setValue } = form;
  const selectedCompanyId = form.watch("clientCompanyId");

  const loadEmployee = useCallback(async () => {
    if (!id) {
      setLoadError("Missing employee ID in the route.");
      setIsLoadingEmployee(false);
      return;
    }

    setIsLoadingEmployee(true);
    setLoadError(null);

    try {
      const response = await authorizedFetch(`/employees/${id}`);

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            response.status === 404
              ? "Employee was not found."
              : `Failed to load employee (${response.status}).`,
          ),
        );
      }

      const employee = (await response.json()) as Employee;
      setEmployeeCompany(
        employee.workstation?.clientCompany ?? employee.clientCompany ?? null,
      );
      setDeletedAt(employee.deletedAt);
      reset(toEmployeeFormValues(employee));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load employee.";
      setLoadError(message);
    } finally {
      setIsLoadingEmployee(false);
    }
  }, [authorizedFetch, id, reset]);

  const loadCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    setLoadCompaniesError(null);

    try {
      setCompanies(await fetchAllClientCompanies(authorizedFetch));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load client companies.";
      setLoadCompaniesError(message);
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    void loadEmployee();
  }, [loadEmployee]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setWorkstations([]);
      setLoadWorkstationsError(null);
      return;
    }

    const loadWorkstations = async () => {
      setIsLoadingWorkstations(true);
      setLoadWorkstationsError(null);

      try {
        const response = await authorizedFetch(
          `/client-companies/${selectedCompanyId}/workstations?includeInactive=true`,
        );

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              `Failed to load workstations (${response.status}).`,
            ),
          );
        }

        const loadedWorkstations = (await response.json()) as Workstation[];
        setWorkstations(loadedWorkstations);

        const currentWorkstationId = getValues("workstationId");
        if (
          currentWorkstationId &&
          loadedWorkstations.some(
            (workstation) => workstation.id === currentWorkstationId,
          )
        ) {
          setValue("workstationId", currentWorkstationId);
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load workstations.";
        setLoadWorkstationsError(message);
        setWorkstations([]);
      } finally {
        setIsLoadingWorkstations(false);
      }
    };

    void loadWorkstations();
  }, [authorizedFetch, getValues, selectedCompanyId, setValue]);

  const onSubmit = async (values: EmployeeFormValues) => {
    if (!id) {
      setSubmitError("Missing employee ID in the route.");
      return;
    }

    if (!canMaintainEmployees) {
      setSubmitError(
        "Only secretary/manager/admin users can update employees.",
      );
      return;
    }

    setSubmitError(null);

    try {
      const response = await authorizedFetch(`/employees/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toUpdateEmployeePayload(values)),
      });

      if (!response.ok) {
        const fallbackMessage =
          response.status === 401 || response.status === 403
            ? "You are not authorized to update employees."
            : response.status === 404
              ? "Employee was not found."
              : response.status === 409
                ? "An employee with this national ID already exists for the selected company."
                : `Failed to update employee (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      const updated = (await response.json()) as Employee;
      toast.success(`Saved changes for ${updated.fullName}.`);
      navigate("/employees");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update employee.";
      setSubmitError(message);
    }
  };

  const companyOptions = useMemo(() => {
    if (!employeeCompany) {
      return companies;
    }

    const alreadyIncluded = companies.some(
      (company) => company.id === employeeCompany.id,
    );

    if (alreadyIncluded) {
      return companies;
    }

    return [employeeCompany, ...companies];
  }, [companies, employeeCompany]);

  const formId = "employee-form";
  const isSubmitting = form.formState.isSubmitting;
  const canSubmitForm = !isLoadingEmployee && !loadError;

  return (
    <PageSectionCard
      titleId="employee-edit-title"
      eyebrow="Employee"
      title="Edit"
      stickyHeader
      actions={
        <div className="headerActions">
          <EntityStatus deletedAt={deletedAt} />
          <DeactivateButton
            inactive={!!deletedAt}
            endpoint={`/employees/${id}`}
            label="Employee"
            returnTo="/employees"
            disabled={
              !canManageClientCompanies || !canSubmitForm || isSubmitting
            }
          />
          <button
            className="primaryButton"
            type="submit"
            form={formId}
            disabled={
              isSubmitting ||
              isLoadingWorkstations ||
              !canMaintainEmployees ||
              !canSubmitForm
            }
          >
            {isSubmitting ? "Saving..." : "Save changes"}
          </button>
        </div>
      }
    >
      {isLoadingEmployee ? <p>Loading employee details...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {loadCompaniesError ? (
        <p className="errorMessage" role="alert">
          Unable to load client-company options: {loadCompaniesError}
        </p>
      ) : null}

      {loadWorkstationsError ? (
        <p className="errorMessage" role="alert">
          Unable to load workstation options: {loadWorkstationsError}
        </p>
      ) : null}

      {!isLoadingEmployee && !loadError ? (
        <EmployeeForm
          id={formId}
          form={form}
          onSubmit={onSubmit}
          submitError={submitError}
          companies={companyOptions}
          workstations={workstations}
          isLoadingCompanies={isLoadingCompanies}
          isLoadingWorkstations={isLoadingWorkstations}
          disableCompanySelection
        />
      ) : null}
    </PageSectionCard>
  );
}

export default EmployeeEditPage;
